import { useRef, useState } from 'react';
import noPhoto from '../../assets/shared/no-photo.jpg';
import { ApiError } from '../../lib/api';
import { mediaApi, mediaUrl } from '../../lib/mediaApi';

/** One photo per object (layer feature or dictionary value) — a fixed attribute
 * of the object itself, not a configurable field. Shows the placeholder when
 * there's no photo yet; when `canEdit`, clicking uploads/replaces it. The
 * caller decides how the new file id gets persisted (layer feature vs
 * dictionary value use different endpoints) via `onChange`. */
export function ObjectPhoto({
  fileId,
  canEdit,
  onChange,
  size = 96,
  className = '',
}: {
  fileId: string | null | undefined;
  canEdit: boolean;
  onChange?: (fileId: string) => Promise<void> | void;
  size?: number;
  className?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const uploaded = await mediaApi.upload(file);
      await onChange?.(uploaded.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить фото.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className={`relative shrink-0 overflow-hidden rounded-lg border border-border-default bg-bg-surface-secondary ${className}`}
      style={{ width: size, height: size }}
    >
      <img src={fileId ? mediaUrl(fileId) : noPhoto} alt="" className="size-full object-cover" />

      {canEdit && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) handleFile(file);
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            title="Загрузить фото"
            className="absolute inset-0 flex items-center justify-center bg-black/0 text-sm text-transparent transition-colors hover:bg-black/40 hover:text-white disabled:bg-black/40 disabled:text-white"
          >
            {uploading ? '…' : '✎'}
          </button>
        </>
      )}

      {error && (
        <span className="absolute inset-x-0 bottom-0 truncate bg-status-danger px-1 py-0.5 text-[9px] font-medium text-text-inverse">
          {error}
        </span>
      )}
    </div>
  );
}
